import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ShieldCheck, Clock4, MailCheck, RefreshCw } from "lucide-react";

/**
 * Account recovery for locked patients.
 *
 * Step 1  Patient enters their email + optional note → backend records the
 *         request and emails the clinic team.
 * Step 2  Patient waits for the clinic to approve (status: pending).
 * Step 3  Once status = approved, patient requests an OTP, then enters it +
 *         a new password. On success they're logged back in automatically.
 */
export default function RecoverAccount() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { loginWith } = useAuth();
  const [email, setEmail] = useState(params.get("email") || "");
  const [reason, setReason] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [status, setStatus] = useState("none"); // none | pending | approved | consumed
  const [decisionNote, setDecisionNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const refreshStatus = useCallback(async (emailArg) => {
    const target = (emailArg || email).trim().toLowerCase();
    if (!target) return;
    try {
      const { data } = await api.post("/auth/recovery-status", { email: target });
      setStatus(data.status);
      setDecisionNote(data.decision_note || "");
    } catch {
      /* silent — keep current status */
    }
  }, [email]);

  // Auto-poll once a minute while pending so the patient sees approval quickly.
  useEffect(() => {
    if (status !== "pending") return undefined;
    const id = setInterval(() => refreshStatus(), 60_000);
    return () => clearInterval(id);
  }, [status, refreshStatus]);

  // If we landed here via the approval email (?email=...), check status straight away.
  useEffect(() => {
    if (params.get("email")) refreshStatus(params.get("email"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/recovery-request", { email: email.trim(), reason });
      toast.success("Request sent. Our clinic team will review it shortly.");
      setStatus("pending");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const requestOtp = async () => {
    setLoading(true);
    try {
      await api.post("/auth/recovery-otp", { email: email.trim() });
      setOtpSent(true);
      toast.success("OTP sent to your email.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/recovery-reset", {
        email: email.trim(), otp: otp.trim(), new_password: newPassword,
      });
      loginWith(data.user);
      toast.success("Password reset — you're back in!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16 md:py-24" data-testid="recover-account-page">
      <div className="bg-white rounded-3xl border border-[#EDF1EF] shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
        <span className="w-12 h-12 rounded-2xl bg-[#D9E4E1] flex items-center justify-center mb-5">
          <ShieldCheck className="w-6 h-6 text-[#4A7065]" />
        </span>
        <h1 className="font-heading text-3xl text-slate-900">Recover your account</h1>
        <p className="text-sm text-slate-500 mt-1">
          Your account was locked after several failed login attempts. Our clinic team will
          verify your request and approve a password reset.
        </p>

        {status === "none" && (
          <form onSubmit={submitRequest} className="mt-7 space-y-4">
            <div>
              <Label htmlFor="recover-email">Registered email</Label>
              <Input
                id="recover-email"
                data-testid="recover-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1.5 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="recover-reason">Anything we should know (optional)</Label>
              <Textarea
                id="recover-reason"
                data-testid="recover-reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. 'Forgot password after last visit'"
                className="mt-1.5 rounded-xl"
                rows={3}
              />
            </div>
            <Button
              data-testid="recover-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] py-6"
            >
              {loading ? "Sending…" : "Send recovery request"}
            </Button>
          </form>
        )}

        {status === "pending" && (
          <div data-testid="recover-pending" className="mt-7 rounded-xl border border-amber-100 bg-amber-50 p-5 text-sm text-amber-900">
            <Clock4 className="w-5 h-5 mb-2 text-amber-700" />
            <p className="font-semibold">Waiting for clinic approval</p>
            <p className="mt-1.5">
              We've notified our team about your request for <b>{email}</b>. You'll receive an
              email as soon as it's approved — usually within a few hours during clinic times.
            </p>
            <Button
              data-testid="recover-refresh-btn"
              onClick={() => refreshStatus()}
              variant="outline"
              size="sm"
              className="mt-4 rounded-full"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Check status
            </Button>
          </div>
        )}

        {status === "approved" && (
          <div data-testid="recover-approved" className="mt-7 space-y-4">
            <div className="rounded-xl border border-green-100 bg-green-50 p-4 text-sm text-green-800">
              <MailCheck className="w-5 h-5 mb-2 text-green-700" />
              <p className="font-semibold">Approved — you can reset your password now.</p>
              {decisionNote && (
                <p className="mt-1.5 text-green-900/80"><b>Note from clinic:</b> {decisionNote}</p>
              )}
            </div>
            {!otpSent ? (
              <Button
                data-testid="recover-otp-btn"
                onClick={requestOtp}
                disabled={loading}
                className="w-full rounded-full bg-[#2C557E] hover:bg-[#22436a] py-6"
              >
                {loading ? "Sending OTP…" : `Send OTP to ${email}`}
              </Button>
            ) : (
              <form onSubmit={submitReset} className="space-y-4">
                <div>
                  <Label htmlFor="recover-otp">OTP from your email</Label>
                  <Input
                    id="recover-otp"
                    data-testid="recover-otp-input"
                    inputMode="numeric"
                    required
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="6-digit code"
                    className="mt-1.5 rounded-xl tracking-[0.5em] text-center"
                  />
                </div>
                <div>
                  <Label htmlFor="recover-newpass">New password</Label>
                  <Input
                    id="recover-newpass"
                    data-testid="recover-newpass-input"
                    type="password"
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="mt-1.5 rounded-xl"
                  />
                </div>
                <Button
                  data-testid="recover-reset-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] py-6"
                >
                  {loading ? "Resetting…" : "Reset password and log in"}
                </Button>
                <button
                  type="button"
                  data-testid="recover-otp-resend"
                  onClick={requestOtp}
                  className="text-xs text-slate-500 hover:text-[#2C557E] block mx-auto"
                >
                  Didn't get it? Resend OTP
                </button>
              </form>
            )}
          </div>
        )}

        <p className="text-sm text-slate-500 text-center mt-6">
          Remembered it after all?{" "}
          <Link to="/login" data-testid="recover-back-login" className="text-[#2C557E] font-medium hover:underline">
            Back to login
          </Link>
        </p>
      </div>
    </div>
  );
}
